import { useCallback, useState } from 'react';

import type { EndorsementFormData } from '../types';

const STATEMENT_MAX_LENGTH = 1000;
const INITIAL_FORM_DATA: EndorsementFormData = { name: '', title: '', statement: '', photoUrl: undefined, url: '' };

export type EndorsementError = {
	message: string;
	type: 'UNAUTHORIZED' | 'VALIDATION' | 'SERVER_ERROR' | 'NETWORK_ERROR';
};

export function useEndorsementForm(initialData?: Partial<EndorsementFormData>) {
	const [formData, setFormData] = useState<EndorsementFormData>({ ...INITIAL_FORM_DATA, ...initialData });
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<EndorsementError | null>(null);
	const [showValidation, setShowValidation] = useState(false);

	const handleFieldChange = useCallback((field: keyof EndorsementFormData, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	}, []);

	const handlePhotoUpload = useCallback((photoUrl: string | undefined) => {
		setFormData((prev) => ({ ...prev, photoUrl }));
	}, []);

	const clearError = useCallback(() => setError(null), []);

	const resetForm = useCallback(() => {
		setFormData({ ...INITIAL_FORM_DATA, ...initialData });
		setError(null);
		setShowValidation(false);
	}, [initialData]);

	const enableValidation = useCallback(() => setShowValidation(true), []);

	return {
		formData,
		isSubmitting,
		error,
		showValidation,
		statementCharCount: formData.statement.length,
		statementMaxLength: STATEMENT_MAX_LENGTH,
		setIsSubmitting,
		setError,
		handleFieldChange,
		handlePhotoUpload,
		clearError,
		resetForm,
		enableValidation,
	};
}
